import axios, { CancelTokenSource } from "axios";
import { paths } from "config/paths";
import { useFormik } from "formik";
import { useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { sendSignUpEvent } from "~tag-manager/send-event";
import { useBoolean } from "usehooks-ts";
import { v3apiInstance } from "~utils/axios";
import { tryCatch } from "~utils/try-catch";

import { usePageLoaded } from "~auth/hooks/use-page-loaded";
import { useSignUpV3 } from "~auth/queries/use-sign-up-v3";

import { toast } from "~ui-components/components/atoms/toaster";
import {
	SignUpV3FormValues,
	SignUpV3RemoteFormValues,
	SignUpV3RemoteSchema,
	SignUpV3Schema,
	SignUpV3 as View,
} from "~ui-components/components/templates/sign-up-v3";
let emailValidationSource: CancelTokenSource | null = null;

export const SignUpV3View = () => {
	const isPageLoaded = usePageLoaded();
	const router = useWebRouter();
	const signUp = useSignUpV3();
	const recaptchaRef = useRef<ReCAPTCHA>(null);
	const form = useFormik<SignUpV3FormValues>({
		validateOnMount: true,
		initialValues: {
			name: "",
			password: "",
			acceptedToTermsAndPolicy: false,
		},
		validationSchema: SignUpV3Schema,
		validate: (values) => {
			if (values.name) {
				const error = validateNameForMaliciousContent(values.name);
				if (error) {
					return {
						name: error,
					};
				}
			}
			return {};
		},
		onSubmit: (values, helpers) => {
			const payload = {
				...values,
				...remoteForm.values,
				email: remoteForm.values.email.toLowerCase(),
			};

			toast.promise(
				async () => {
					let recaptchaToken: string | null = null;
					try {
						recaptchaToken =
							(await recaptchaRef.current?.executeAsync()) ?? null;
					} catch {
						// executeAsync itself rejected (e.g. no google.com
						// reachability). Normalize to the same client-facing
						// message the missing-token branch uses so the UI does
						// not need to discriminate the failure mode.
					}
					if (!recaptchaToken) {
						throw new Error("Please complete the verification and try again.");
					}

					const [error] = await tryCatch(
						signUp.mutateAsync({ ...payload, recaptchaToken }),
					);
					if (error) {
						throw error;
					}

					sendSignUpEvent();

					void router.replace(
						paths.signUpVerificationV3 + "?email=" + payload.email,
					);
				},
				{
					loading: "Signing you up...",
					success: "Success!",
					error: (e) => {
						// Every failure path — executeAsync rejection, missing
						// token, and signUp rejection — funnels here exactly
						// once. Unblock the submit button and clear the captcha
						// widget so the next attempt starts from scratch.
						helpers.setSubmitting(false);
						recaptchaRef.current?.reset();

						if (axios.isAxiosError(e)) {
							const data = e.response?.data;
							const message =
								data &&
								typeof data === "object" &&
								"message" in data &&
								typeof (data as { message?: unknown }).message === "string"
									? (data as { message: string }).message
									: null;
							return message ?? "Something went wrong!";
						}
						if (e instanceof Error && e.message) return e.message;
						return "Something went wrong!";
					},
				},
			);
		},
	});
	const remoteForm = useFormik<SignUpV3RemoteFormValues>({
		validateOnMount: true,
		validateOnBlur: false,
		validateOnChange: false,
		initialValues: {
			email: "",
		},
		validationSchema: SignUpV3RemoteSchema,
		// eslint-disable-next-line @typescript-eslint/no-empty-function -- only use this form for validation
		onSubmit: () => undefined,
	});
	const { value: isRemotelyValidatingEmail, setValue: toggleValidatingEmail } =
		useBoolean(false);

	const handleEmailValueChange = async (newValue: string) => {
		const [schemaErrorMessage] = await Promise.all([
			SignUpV3RemoteSchema.fields.email
				.validate(newValue)
				.then(() => null)
				.catch((e) => e.message as string),
			remoteForm.setFieldValue("email", newValue),
			remoteForm.setFieldTouched("email", true),
		]);
		if (schemaErrorMessage) {
			remoteForm.setFieldError("email", schemaErrorMessage);
			return;
		}

		toggleValidatingEmail(true);
		const [remoteError] = await tryCatch(remotelyValidateEmail(newValue));
		if (remoteError) {
			if (axios.isCancel(remoteError)) return;

			toggleValidatingEmail(false);
			remoteForm.setFieldError("email", remoteError.message);
			return;
		}
		toggleValidatingEmail(false);
		remoteForm.setFieldError("email", undefined);
	};

	if (!isPageLoaded) return null;

	return (
		<>
			<View
				form={form}
				remoteForm={remoteForm}
				onEmailChange={(e) => handleEmailValueChange(e.target.value)}
				onEmailBlur={(e) => handleEmailValueChange(e.target.value)}
				// onNameBlur={(e) => handleNameBlur(e.target.value)}
				isRemotelyValidatingEmail={isRemotelyValidatingEmail}
			/>
			<ReCAPTCHA
				ref={recaptchaRef}
				size="invisible"
				sitekey={process.env.GOOGLE_RECAPTCHA_SITE_KEY ?? ""}
			/>
		</>
	);
};

const remotelyValidateEmail = async (value: string) => {
	if (emailValidationSource) emailValidationSource.cancel();
	emailValidationSource = axios.CancelToken.source();

	const [error, data] = await tryCatch(
		v3apiInstance.get<{
			portal_url: string | null;
			status: string;
			users: Record<string, any>[];
		}>(`/account/search?term=${encodeURIComponent(value)}&qc=email`, {
			cancelToken: emailValidationSource.token,
		}),
	);

	if (error) {
		if (axios.isCancel(error)) throw error;
		if (axios.isAxiosError(error)) throw new Error("Something went wrong!");
		throw new Error(error.message);
	}
	if (data.users.length > 0)
		throw new Error(
			"This email is already in use. Please use another email address.",
		);
};

const validateNameForMaliciousContent = (name: string): string | null => {
	// Prevent HTML tags, script injection, and other malicious patterns
	const maliciousPatterns = [
		/<[^>]*>/g, // HTML tags
		/javascript:/gi, // JavaScript protocol
		/on\w+\s*=/gi, // Event handlers (onclick, onerror, etc.)
		/\\x[0-9a-f]{2}/gi, // Hex encoding
		/&#/g, // HTML entities
	];

	// Check for malicious patterns
	for (const pattern of maliciousPatterns) {
		if (pattern.test(name)) {
			return "Name can only contain letters, numbers, spaces, and apostrophes";
		}
	}

	// Allow letters, numbers, spaces, apostrophes, hyphens, and common international characters
	// This supports names like "Mary-Jane", "O'Brien", "José", "François", etc.
	const validPattern = /^[\p{L}\p{N}\s'-]+$/u;

	if (!validPattern.test(name)) {
		return "Name can only contain letters, numbers, spaces, and apostrophes";
	}

	return null;
};
